import Accordion from "@/common/components/Accordion"
import AddressForm from "@/common/components/AddressForm"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import { ImageUploader } from "@/common/components/ImageUploader"
import MaskedTextField from "@/common/components/MaskedTextField"
import TextField from "@/common/components/TextField"
import { cnpjCpfMask, stateInscriptionMask } from "@/common/constants/masks.constant"
import { useVaduCnpjDetails } from "@/common/hooks/queries/useVaduCnpjDetails"
import useAddressForm from "@/common/hooks/useAddressForm"
import { AddressValidationSchema } from "@/common/schemas/address.schema"
import { AddressData } from "@/common/types/address"
import { VaduCnpjDetailsResponse } from "@/common/types/vaduCnpjDetails.type"
import { checkIsImage } from "@/common/utils/fileValidator"
import { formatCnpj } from "@/common/utils/formatters"
import { unFormatCnpj } from "@/common/utils/unFormatters"
import { strategy as kindStrategy } from "@/features/companies/constants/kind.constant"
import { useEffect, useState } from "react"
import { Controller, FieldErrors, useWatch } from "react-hook-form"
import { useCompanyBranch } from '../../hooks/useCompanyBranch'
import { CompanyBranchValidationSchema } from "../../schemas/createBranch.schema"
import { GeneralInformationProps } from "../../types/generalInformation"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"

export const GeneralInformation = ({
  branchData,
  parentId,
  file,
  setFile,
  register,
  errors,
  control,
  setValue,
  disableCnpj,
  watch,
  resetField,
  companyData
}: GeneralInformationProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const generalInformationAttributes = watch('general_information');

  const selectedParent = useWatch({
    control,
    name: "parent.value",
  });

  const willUseOwnData = useWatch({
    control,
    name: "general_information.willUseOwnData",
  });

  const { data: parentData, dataUpdatedAt: parentDataUpdatedAt } = useCompanyBranch(selectedParent || '', !!selectedParent);

  const { mutate: getVaduCnpjDetails, isPending: vaduCnpjDetailsIsPending } = useVaduCnpjDetails({
    onSuccess: (data) => successGetVaduCnpjDetails(data),
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null)
    },
  });

  const handleImportCnpjData = () => {
    getVaduCnpjDetails(unFormatCnpj(generalInformationAttributes?.cnpj || ''));
  };

  const successGetVaduCnpjDetails = (data: VaduCnpjDetailsResponse) => {
    const { general_information } = data.data.company;

    setValue('general_information', {
      ...generalInformationAttributes,
      willUseOwnData: true,
      cnpj: general_information.cnpj || '',
      fantasy_name: general_information.fantasy_name || '',
      official_name: general_information.official_name || '',
      email: general_information.email || '',
      primary_cnae: unFormatCnpj(general_information.primary_cnae || ''),
    });
  };

  const branchFormData = willUseOwnData ? branchData?.general_information : parentData?.general_information || companyData?.general_information

  const addressForm = useAddressForm<CompanyBranchValidationSchema>({
    initialAddress: generalInformationAttributes?.address as AddressData || {},
    path: 'general_information.address_attributes',
    disabled: !willUseOwnData,
  });

  const { setAddress } = addressForm;

  useEffect(() => {
    if (willUseOwnData && !branchFormData && resetField) {
      resetField('general_information', {
        defaultValue: {
          willUseOwnData: true,
          cnpj: null,
          email: null,
          fantasy_name: null,
          official_name: null,
          primary_cnae: null,
          state_inscription: null,
          address_attributes: {},
        },
      });
    } else {
      let newFormParams = { ...generalInformationAttributes, ...branchFormData, address_attributes: branchFormData?.address }
      newFormParams.cnpj = formatCnpj(newFormParams.cnpj + '')
      setValue('general_information', newFormParams)
    }
  }, [branchFormData]);

  useEffect(() => {
    setAddress((willUseOwnData ? branchFormData?.address as AddressData : watch('general_information')?.address as AddressData) || {});
  }, [willUseOwnData, parentId]);

  useEffect(() => {
    if (!willUseOwnData) {
      let newFormParams = { ...generalInformationAttributes, ...branchFormData, address_attributes: branchFormData?.address }
      newFormParams.cnpj = formatCnpj(newFormParams.cnpj + '')
      setValue('general_information', newFormParams)
    }
  }, [parentDataUpdatedAt]);

  return (
    <Accordion
      name="general_information"
      contentClassName="data-[state=open]:overflow-visible"
      head={(
        <div className="flex flex-col gap-2 ">
          {errors.general_information?.willUseOwnData && (
            <p className="text-sm text-danger-soft" data-testid="willUseOwnData_error_message">
              {errors.general_information.willUseOwnData.message}
            </p>
          )}
          <Checkbox
            label="Dados empresariais específicos"
            disabled={!parentId}
            onClick={(event) => {
              event.stopPropagation();
            }}
            {...register("general_information.willUseOwnData")}
          />
        </div>
      )}
      body={(
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            <div>
              <ImageUploader
                isLoading={false}
                label="Logo"
                name="profile-picture"
                accept="image/*"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
                  if (!file || !checkIsImage(file)) {
                    return
                  }
                  setFile(file)
                }}
                currentImageUrl={file
                  ? URL.createObjectURL(file)
                  : branchFormData?.company_logo?.url
                    ? branchFormData?.company_logo.url
                    : undefined
                }
                disabled={!willUseOwnData}
              />
            </div>
            <div className="flex gap-2 w-full">
              <div className="flex flex-col w-1/2 items-start gap-y-1">
                <Controller
                  name={`general_information.cnpj`}
                  control={control}
                  disabled={!willUseOwnData || disableCnpj || !parentId}
                  render={({ field }) => (
                    <MaskedTextField
                      mask={cnpjCpfMask}
                      className="w-full"
                      label="CNPJ/CPF"
                      markAsRequired
                      disabled={!willUseOwnData || disableCnpj || !parentId}
                      errorMessage={errors?.general_information?.cnpj?.message || undefined}
                      placeholder={!willUseOwnData ? companyData?.matrix_branch?.general_information?.cnpj : ''}
                      {...register('general_information.cnpj')}
                      {...field}
                    />
                  )}
                />

                <Button
                  name="import_cnpj_data"
                  variant="link"
                  onClick={handleImportCnpjData}
                  disabled={
                    !willUseOwnData ||
                    disableCnpj ||
                    !parentId ||
                    !(generalInformationAttributes?.cnpj && generalInformationAttributes?.cnpj?.length >= 18) ||
                    vaduCnpjDetailsIsPending
                  }
                  isLoading={vaduCnpjDetailsIsPending}
                >
                  Importar dados
                </Button>
              </div>

              <TextField
                label="Tipo de empresa"
                value={companyData?.kind ? kindStrategy[companyData.kind as keyof typeof kindStrategy] : ''}
                className="w-1/2"
                disabled
              />
            </div>
          </div>
          <div className="flex gap-2">
            <TextField
              label="Nome Fantasia"
              markAsRequired
              className="w-1/2"
              maxLength={64}
              disabled={!willUseOwnData}
              placeholder={!willUseOwnData ? companyData?.general_information?.fantasy_name : ''}
              errorMessage={errors?.general_information?.fantasy_name?.message || undefined}
              defaultValue={generalInformationAttributes?.fantasy_name || ''}
              {...register('general_information.fantasy_name')}
            />
            <TextField
              label="Nome Empresarial"
              markAsRequired
              className="w-1/2"
              maxLength={64}
              disabled={!willUseOwnData}
              placeholder={!willUseOwnData ? companyData?.general_information?.official_name : ''}
              errorMessage={errors?.general_information?.official_name?.message || undefined}
              {...register('general_information.official_name')}
            />
          </div>
          <TextField
            label="Endereço eletrônico"
            markAsRequired
            maxLength={64}
            errorMessage={errors?.general_information?.email?.message || undefined}
            disabled={!willUseOwnData}
            placeholder={!willUseOwnData ? companyData?.general_information?.email : ''}
            {...register('general_information.email')}
          />
          <div className="flex gap-2">
            <Controller
              name={`general_information.state_inscription`}
              control={control}
              disabled={!willUseOwnData}
              render={({ field }) => (
                <MaskedTextField
                  label="IE (Inscrição Estadual)"
                  markAsRequired
                  mask={stateInscriptionMask}
                  errorMessage={errors?.general_information?.state_inscription?.message || undefined}
                  className="w-1/2"
                  disabled={!willUseOwnData}
                  placeholder={!willUseOwnData ? companyData?.general_information?.state_inscription : ''}
                  {...register('general_information.state_inscription')}
                  {...field}
                />
              )}
            />
            <TextField
              label="CNAE"
              markAsRequired
              maxLength={7}
              errorMessage={errors?.general_information?.primary_cnae?.message || undefined}
              className="w-1/2"
              disabled={!willUseOwnData}
              placeholder={!willUseOwnData ? companyData?.general_information?.primary_cnae : ''}
              {...register('general_information.primary_cnae')}
            />
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <p className="text-[18px] font-bold">
                Endereço
              </p>
            </div>

            <AddressForm
              addressForm={addressForm}
              disableFields={!willUseOwnData}
              errors={errors?.general_information?.address_attributes as FieldErrors<AddressValidationSchema>}
              markFieldsAsRequired
            />
          </div>

          {messages && (
            <ModalMessageSimple
              messages={messages}
              setMessages={setMessages}
              isError
            />
          )}
        </div>
      )}
    />
  )
}
